import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Upload, Eye, Trash2, FileCheck, FolderOpen, Loader2 } from "lucide-react";
import DepositoIcertiRes022 from "./DepositoIcertiRes022";

// Clone of DocumentosAdicionales.jsx, isolated on purpose so RES022 work
// can't affect the existing RES020 additional-documents flow.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentosAdicionalesRes022({ dossierId }) {
  const [slots, setSlots] = useState([]);
  const [documents, setDocuments] = useState({});
  const [uploading, setUploading] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/documentos-adicionales-res022/${dossierId}`);
      if (!res.ok) {
        console.warn(`[DocumentosAdicionalesRes022] API error ${res.status} for dossier ${dossierId}`);
        return;
      }
      const data = await res.json();
      setSlots(data.slots || []);
      setDocuments(data.documents || {});
    } catch (err) {
      console.error("[DocumentosAdicionalesRes022] fetch error:", err);
    }
  }, [dossierId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (slotKey, file) => {
    setUploading(slotKey);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${API}/documentos-adicionales-res022/${dossierId}/${slotKey}`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error();
      toast.success("Documento subido");
      fetchDocs();
    } catch {
      toast.error("Error al subir documento");
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (slotKey) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      const res = await fetch(`${API}/documentos-adicionales-res022/${dossierId}/${slotKey}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error();
      toast.success("Documento eliminado");
      fetchDocs();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const handleView = async (slotKey) => {
    setViewing(slotKey);
    try {
      const res = await fetch(`${API}/documentos-adicionales-res022/${dossierId}/${slotKey}/download`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      toast.error("Error al abrir documento");
    } finally {
      setViewing(null);
    }
  };

  if (!slots.length) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FolderOpen className="w-5 h-5" />
          Documentos Adicionales
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <DepositoIcertiRes022 dossierId={dossierId} onDone={fetchDocs} />
        {slots.map((slot) => {
          const doc = documents[slot.key];
          const isUploading = uploading === slot.key;
          const isViewing = viewing === slot.key;
          return (
            <div key={slot.key} className="flex items-center gap-3" data-testid={`slot-res022-${slot.key}`}>
              <div className="flex-1 min-w-0">
                {doc ? (
                  <div className="flex items-center justify-between w-full p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-emerald-700 text-sm font-medium">{slot.label}</div>
                        <div className="text-emerald-600 text-xs break-all">{doc.filename}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => handleView(slot.key)} disabled={isViewing} className="p-1.5 text-slate-600 hover:bg-white rounded disabled:opacity-50" title="Ver" data-testid={`view-res022-${slot.key}`}>
                        {isViewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <label className="p-1.5 text-sky-600 hover:bg-white rounded cursor-pointer" title="Reemplazar">
                        <Upload className="w-4 h-4" />
                        <input type="file" className="hidden" onChange={(e) => { if (e.target.files[0]) handleUpload(slot.key, e.target.files[0]); e.target.value = ""; }} />
                      </label>
                      <button onClick={() => handleDelete(slot.key)} className="p-1.5 text-red-500 hover:bg-white rounded" title="Eliminar" data-testid={`delete-res022-${slot.key}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className={`flex items-center justify-center gap-2 w-full p-2 text-sm rounded-lg border border-dashed border-slate-300 hover:border-sky-400 hover:bg-sky-50/50 cursor-pointer transition-colors ${isUploading ? "opacity-50 pointer-events-none" : "text-slate-400 hover:text-sky-600"}`}>
                    <Upload className="w-3.5 h-3.5" />
                    {isUploading ? "Subiendo..." : slot.label}
                    <input type="file" className="hidden" disabled={isUploading} onChange={(e) => { if (e.target.files[0]) handleUpload(slot.key, e.target.files[0]); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
